import { filterFalsyValues } from '@/views/filter/utils';
import {
  emptyFilterCriteria,
  type FilterCriteria,
  type FilterState,
  type FuelCategoryThresholds,
} from './filterSlice';
import isEqual from 'lodash/isEqual';
import { type GroupId, WITH_OVERHANG } from '@/features/types';
import {
  FuelCategoryBucket,
  type FuelCategoryThreshold,
  type GroupAnalysisFilter,
} from '@/generated/types/navigator/types.gen';

// Hardcoded to avoid a circular dependency on filterSlice's runtime exports
// (filterSlice imports this module for isFilterEmpty / pickFilterCriteria).
const FUEL_CATEGORY_KEYS: Array<keyof FuelCategoryThresholds> = [
  'grass',
  'shrub',
  'timber_litter',
  'slash_blowdown',
  'non_burnable',
];

const VALID_BUCKETS = Object.values(FuelCategoryBucket);

const isValidBucket = (raw: string): raw is FuelCategoryBucket =>
  (VALID_BUCKETS as string[]).includes(raw);

/**
 * Adds a group to the filter query for the given network group
 */
export function addGroupToFilter(
  groupName: string,
  selectedNetworkGroup: { id: GroupId } | null,
  filterQuery: FilterState
): FilterState {
  if (!selectedNetworkGroup) return filterQuery;

  return {
    ...filterQuery,
    groups: {
      ...filterQuery.groups,
      [selectedNetworkGroup.id]: [groupName],
    },
  };
}

/**
 * Type-safe function to pick only FilterCriteria properties from FilterState
 * Uses emptyFilterCriteria as the source of truth for which properties to include
 */
export const pickFilterCriteria = (state: FilterState): FilterCriteria => {
  const filterKeys = Object.keys(emptyFilterCriteria) as Array<
    keyof FilterCriteria
  >;

  const entries = filterKeys.map((key) => {
    const value =
      key === 'groups'
        ? filterFalsyValues(state[key]) // Special handling for groups
        : state[key];

    return [key, value] as const;
  });

  return Object.fromEntries(entries) as unknown as FilterCriteria;
};

export const isFilterEmpty = (filterState: FilterState) => {
  const currentCriteria = pickFilterCriteria(filterState);
  return isEqual(currentCriteria, emptyFilterCriteria);
};

export const getFilterGroupIdParam = (
  filter: FilterState,
  group_id: GroupId
) => {
  // When a user has selected a group (activeGroupName)
  // fetch span data without changing selectedNetworkGroup or selectedNetworkGroupId
  // because changing selectedNetworkGroup will change what is rendered on the mapview
  // and means that if a user navigates back up, they would see spans and not their selected network group
  return filter.activeGroupName && filter.spanNetworkGroupId
    ? filter.spanNetworkGroupId
    : group_id;
};

/**
 * A group key with no selection carries `undefined`, which JSON serialization
 * drops. Removing those entries up front keeps the payload type honest about
 * what actually reaches the server.
 */
const definedGroups = (
  groups: FilterState['groups']
): Record<string, (string | null)[]> =>
  Object.fromEntries(
    Object.entries(groups).filter(
      (entry): entry is [string, (string | null)[]] => entry[1] !== undefined
    )
  );

/** Formats a filter query for a server request */
export const getFilterQuery = (filter: FilterState): GroupAnalysisFilter => {
  const overhangFilter =
    filter.encroachmentType.length === 1
      ? { hasOverhang: filter.encroachmentType[0] === WITH_OVERHANG }
      : {};

  const activeFuelCategoryThresholds = FUEL_CATEGORY_KEYS.reduce<
    FuelCategoryThreshold[]
  >((acc, category) => {
    for (const raw of filter.fuelCategoryThresholds[category]) {
      if (isValidBucket(raw)) acc.push({ category, bucket: raw });
    }
    return acc;
  }, []);
  const fuelCategoryThresholdsFilter =
    activeFuelCategoryThresholds.length > 0
      ? { fuelCategoryThresholds: activeFuelCategoryThresholds }
      : {};

  return {
    network: filter.network === 'all' ? [1, 2] : [parseInt(filter.network)],
    encroachmentRatings: filter.encroachmentRatings.map((i: string) =>
      parseInt(i)
    ),
    voltageInKv: filter.voltageInKv.map((i: string) => parseFloat(i)),
    phasingType: filter.phasingType,
    groups: definedGroups(filter.groups),
    species: filter.species.map((i: string) => parseInt(i)),
    wildfireRatings: filter.wildfireRatings.map((i: string) => parseInt(i)),
    redFlagWarnings: filter.redFlagWarnings,
    hasHazardTrees: filter.hasHazardTrees,
    hasStrikeTrees: filter.hasStrikeTrees,
    hasCriticalTrees: filter.hasCriticalTrees,
    ...overhangFilter,
    tags: filter.tags.map((i: string) => parseInt(i)),
    priorityRatings: filter.priorityRatings.map((i: string) => parseInt(i)),
    wildfirePriorityRatings: filter.wildfirePriorityRatings.map((i: string) =>
      parseInt(i)
    ),
    workStatus: filter.workStatus.map((i: string) => i),
    updatedAtYears: filter.updatedAtYears,
    fuelHazardRatings: filter.fuelHazardRatings.map((i: string) => parseInt(i)),
    ...fuelCategoryThresholdsFilter,
  };
};

/**
 * Takes a GroupAnalysisFilter and encodes it for use in an API request
 */
export const encodeFilterQuery = (filterQuery: GroupAnalysisFilter): string => {
  const filterQueryString = JSON.stringify(filterQuery);
  return btoa(filterQueryString);
};
/**
 * Takes a FilterState and encodes it for use in an API request
 */
export const generateBase64FilterQuery = (filter: FilterState): string => {
  const filterQuery = getFilterQuery(filter);
  return encodeFilterQuery(filterQuery);
};
